import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// Pagination page size
const RESULT_PAGE_SIZE = 48;

// ================ Action types ================ //

export const QUERY_FAVOURITES_REQUEST =
  'app/FavouriteListingsPage/QUERY_FAVOURITES_REQUEST';
export const QUERY_FAVOURITES_SUCCESS =
  'app/FavouriteListingsPage/QUERY_FAVOURITES_SUCCESS';
export const QUERY_FAVOURITES_ERROR =
  'app/FavouriteListingsPage/QUERY_FAVOURITES_ERROR';

// ================ Initial state ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryFavouritesError: null,
  currentPageResultIds: [],
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case QUERY_FAVOURITES_REQUEST:
      return {
        ...state,
        queryParams: payload,
        queryInProgress: true,
        queryFavouritesError: null,
        currentPageResultIds: [],
      };

    case QUERY_FAVOURITES_SUCCESS:
      return {
        ...state,
        queryInProgress: false,
        currentPageResultIds: payload.resultIds,
        pagination: payload.pagination,
      };

    case QUERY_FAVOURITES_ERROR:
      return {
        ...state,
        queryInProgress: false,
        queryFavouritesError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const favouriteListingsInProgress = state =>
  state.FavouriteListingsPage.queryInProgress;

// ================ Action creators ================ //

export const queryFavouritesRequest = params => ({
  type: QUERY_FAVOURITES_REQUEST,
  payload: params,
});

export const queryFavouritesSuccess = (resultIds, pagination) => ({
  type: QUERY_FAVOURITES_SUCCESS,
  payload: { resultIds, pagination },
});

export const queryFavouritesError = error => ({
  type: QUERY_FAVOURITES_ERROR,
  payload: error,
  error: true,
});

// ================ Helpers ================ //

const resultIds = data => data.data.map(l => l.id);

// ================ Thunks ================ //

export const queryFavouriteListings = queryParams => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryFavouritesRequest(queryParams));

  const { currentUser } = getState().user;
  const favourites =
    currentUser?.attributes?.profile?.privateData?.favourites || [];

  if (!favourites.length) {
    dispatch(
      queryFavouritesSuccess([], {
        page: 1,
        perPage: RESULT_PAGE_SIZE,
        totalItems: 0,
        totalPages: 0,
      })
    );
    return Promise.resolve();
  }

  const { perPage, ...rest } = queryParams;

  const params = {
    ids: favourites,
    ...rest,
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(
        queryFavouritesSuccess(
          resultIds(response.data),
          response.data.meta
        )
      );
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      console.error(error);
      dispatch(queryFavouritesError(error));
      throw error;
    });
};


// ================ Load data (SSR / route) ================ //

export const loadData = (params, search, config) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  return queryFavouriteListings({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(
      variantPrefix,
      400,
      aspectRatio
    ),
    ...createImageVariantConfig(
      `${variantPrefix}-2x`,
      800,
      aspectRatio
    ),
    'limit.images': 1,
  });
};
